import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import AIAnswerBlock from "../../../components/feature/sections/AIAnswerBlock";
import ProcessSection from "../../../components/feature/sections/ProcessSection";
import WhyChooseUs from "../../../components/feature/sections/WhyChooseUs";
import ServiceAreasSection from "../../../components/feature/sections/ServiceAreasSection";
import TestimonialsSection from "../../../components/feature/sections/TestimonialsSection";
import FAQSection from "../../../components/feature/sections/FAQSection";
import FinalCTA from "../../../components/feature/sections/FinalCTA";

const repairServices = [
  {
    icon: "ri-drop-line",
    title: "Leak Repair",
    description: "Pinpoint leak sources and permanently stop water intrusion before structural damage occurs.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Shingle Replacement",
    description: "Replace missing, cracked, or curling shingles with high-quality materials matched to your roof.",
  },
  {
    icon: "ri-shield-flash-line",
    title: "Flashing Repairs",
    description: "Fix damaged or deteriorated flashing around chimneys, vents, and valleys to prevent leaks.",
  },
  {
    icon: "ri-thunderstorms-line",
    title: "Storm Damage Fixes",
    description: "Rapid response to hail, wind, and storm damage to prevent further deterioration and insurance issues.",
  },
];

const repairFAQs = [
  {
    question: "How do I know if my roof needs repair?",
    answer: "Signs include leaks, missing shingles, water stains on ceilings, sagging areas, or visible damage. A professional inspection from Thomas Roofing & Repair confirms the issue and prevents further problems from developing.",
  },
  {
    question: "How much does roof repair cost in Colorado Springs?",
    answer: "Repair costs vary by damage type and extent. Minor repairs are often affordable, while significant storm or structural damage costs more. We provide free, transparent estimates so you know exactly what you're paying for.",
  },
  {
    question: "Can you fix a roof leak quickly?",
    answer: "Yes. Thomas Roofing & Repair offers fast response for leak repairs, often providing same-day or next-day service. We understand that a leak is urgent and treat it as a priority.",
  },
  {
    question: "Do you repair all types of roofs?",
    answer: "We repair asphalt shingles, flat roofs, and other common residential roof types throughout Colorado Springs and surrounding areas. Contact us to discuss your specific roof material.",
  },
  {
    question: "Will my homeowner's insurance cover roof repair?",
    answer: "Storm damage repairs are often covered by homeowner's insurance. We can document damage thoroughly to help support your insurance claim. Contact us for a detailed inspection report.",
  },
];

export default function RoofRepairPage() {
  return (
    <div className="font-body">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top"
            style={{
              backgroundImage: `url(https://public.readdy.ai/ai/img_res/f04b2efe1a8fa28fca6d29a4d04246df.jpg)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/40" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-32 pt-40">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 mb-6 text-xs text-white/50 font-body">
                <Link to="/" className="hover:text-white/80 cursor-pointer whitespace-nowrap transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-white/70">Roof Repair</span>
              </nav>
              <div className="inline-flex items-center gap-2 bg-[#10B981]/20 border border-[#10B981]/40 rounded-full px-4 py-1.5 mb-8">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-tools-fill text-[#10B981] text-xs"></i>
                </div>
                <span className="text-[#10B981] font-body font-600 text-xs tracking-wider">Expert Repair Services</span>
              </div>
              <h1 className="font-headline font-900 text-white text-4xl lg:text-5xl xl:text-6xl leading-tight mb-6">
                Roof Repair Services That{" "}
                <span className="text-[#10B981]">Stop Leaks and Prevent Costly Damage</span>
              </h1>
              <p className="text-white/80 font-body text-lg leading-relaxed mb-8 max-w-2xl">
                Fast, accurate roof repair for Colorado Springs homeowners. We identify the problem, fix it right, and make sure it doesn't come back.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/#contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-blue-700 text-white font-body font-700 text-base px-8 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  Get a Free Roof Inspection
                </Link>
                <a
                  href="tel:+17195550100"
                  className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-body font-600 text-base px-8 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-fill text-[#10B981] text-sm"></i>
                  </div>
                  Call (719) 555-0100
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Bar */}
        <section className="bg-[#1A1A1A] border-b border-white/10 py-5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center lg:justify-between gap-5">
              {[
                { icon: "ri-flashlight-fill", text: "Same-Day Emergency Response", color: "text-[#10B981]" },
                { icon: "ri-star-fill", text: "5-Star Rated Repair Service", color: "text-amber-400" },
                { icon: "ri-shield-check-fill", text: "Licensed & Insured", color: "text-[#10B981]" },
                { icon: "ri-price-tag-3-fill", text: "Transparent Pricing", color: "text-[#10B981]" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} ${item.color} text-base`}></i>
                  </div>
                  <span className="text-white/80 font-body font-500 text-sm whitespace-nowrap">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Repair Services */}
        <section className="bg-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
                What We Fix
              </span>
              <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
                Roof Repair Solutions
              </h2>
              <p className="text-[#6B7280] font-body text-base lg:text-lg max-w-2xl mx-auto">
                From minor leaks to major storm damage — we handle every type of residential roof repair with precision.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {repairServices.map((svc) => (
                <div key={svc.title} className="p-6 rounded-lg border border-gray-100 hover:border-[#10B981]/40 transition-all duration-200">
                  <div className="w-11 h-11 flex items-center justify-center rounded-md bg-[#10B981]/10 mb-4">
                    <i className={`${svc.icon} text-[#10B981] text-xl`}></i>
                  </div>
                  <h3 className="font-headline font-700 text-[#1A1A1A] text-lg mb-2">{svc.title}</h3>
                  <p className="text-[#6B7280] font-body text-sm leading-relaxed">{svc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <AIAnswerBlock
          heading="What Is Roof Repair?"
          intro="Roof repair involves identifying and fixing issues like leaks, damaged shingles, or structural weaknesses. Timely repairs prevent further damage and significantly extend the lifespan of your roof, protecting your home and its value."
          items={[
            { question: "When Is Repair Needed?", answer: "When shingles are missing, damaged, or curling — or when you notice leaks, water stains, or visible deterioration that compromise your roof's integrity." },
            { question: "Why Act Quickly?", answer: "Small problems escalate fast. A minor leak left unaddressed can cause structural damage, mold growth, and expensive interior damage in weeks." },
            { question: "What's the Outcome?", answer: "Properly repaired roofs protect your home from weather, prevent future damage, and can extend the life of your entire roofing system by years." },
          ]}
        />

        <ProcessSection
          heading="How Our Roof Repair Works"
          subtitle="A clear, straightforward process designed to solve your problem efficiently and permanently."
        />

        <WhyChooseUs />

        <ServiceAreasSection
          geoCopy="Roof repair services available throughout Colorado Springs and nearby areas. If you're searching for roof repair near you, Thomas Roofing & Repair provides fast and reliable service to all residential neighborhoods across El Paso County."
        />

        <TestimonialsSection heading="Customers Trust Our Repair Work" />

        <FAQSection
          heading="Roof Repair Questions Answered"
          items={repairFAQs}
        />

        <FinalCTA
          heading="Stop the Damage Before It Gets Worse"
          subtitle="Get a free repair inspection from Colorado Springs' trusted roofing experts. Fast response, honest pricing."
        />
      </main>
      <Footer />
    </div>
  );
}
